// Strict Enums
export type ContractType = 'EPC' | 'AMC' | 'O&M' | 'SLA' | 'TURNKEY' | 'SERVICE_CONTRACT';
export type ScopeSystem = 'HVAC' | 'ELECTRICAL' | 'PLUMBING' | 'FIRE_SAFETY' | 'BMS' | 'ELV';

// Database Models (snake_case)
export interface DbContract {
  id: string;
  contract_number: string;
  name: string;
  client_name: string;
  client_primary_contact_name: string | null;
  client_primary_contact_phone: string | null;
  client_primary_contact_email: string | null;
  contract_type: ContractType;
  start_date: string;
  planned_end_date: string;
  actual_end_date: string | null;
  status: 'ACTIVE' | 'COMPLETED' | 'ARCHIVED' | 'PARTIALLY_CONFIGURED';
  last_modified: string;
  contract_value: number;
  contract_value_currency: string;
  retention_percentage: number;
  performance_guarantee: number;
  country: string;
  state: string;
  city: string;
  pincode: string;
  address: string;
  organization_id: string;
  organization_name: string;
  contract_admin_user_id: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by: string | null;
  completed_at: string | null;
  completed_by: string | null;
  archived_at: string | null;
  archived_by: string | null;
  archive_reason: string | null;
}

export interface DbContractScopeSystem {
  contract_id: string;
  system: ScopeSystem;
}

export interface DbContractTeamAssignment {
  id: string;
  contract_id: string;
  user_id: string;
  role: 'PROJECT_DIRECTOR' | 'QA_MANAGER';
  assigned_at: string;
}

export interface DbContractClientRepresentative {
  id: string;
  contract_id: string;
  user_id: string;
  assigned_at: string;
}

export interface DbContractProjectSite {
  contract_id: string;
  site_id: string;
  assigned_at: string;
  assigned_by: string;
}

export interface DbContractRevision {
  id: string;
  contract_id: string;
  revision_number: number;
  changed_at: string;
  changed_by: string;
  change_severity: 'MINOR' | 'MAJOR' | 'CRITICAL';
}

export interface DbContractRevisionChange {
  revision_id: string;
  field: string;
  old_value: string;
  new_value: string;
}

export interface DbContractAdminHistory {
  id: string;
  contract_id: string;
  user_id: string;
  full_name_snapshot: string;
  designation_snapshot: string;
  email_snapshot: string;
  assigned_at: string;
  assigned_by: string;
  transfer_reason: string;
  revoked_at: string | null;
}

export interface DbContractRelationAudit {
  id: string;
  contract_id: string;
  relation_type: 'SYSTEM' | 'TEAM' | 'CLIENT_REP' | 'PROJECT_SITE';
  action: 'ADD' | 'REMOVE';
  record_id: string;
  changed_at: string;
  changed_by: string;
}

export interface DbContractMetrics {
  contract_id: string;
  approved_submissions: number;
  total_submissions: number;
  completed_sites: number;
  total_sites: number;
  valid_equipment: number;
  total_equipment: number;
  pending_signatures_total: number;
  pending_signatures_overdue: number;
}

export interface ProjectSiteMaster {
  site_id: string;
  site_name: string;
  status: 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';
}

export interface UserMaster {
  id: string;
  name: string;
  role: 'ADMIN' | 'CONTRACTOR_REP' | 'CLIENT_REP' | 'OEM_REP';
  email: string;
  designation: string;
}

// Master tables
export const mockProjectSitesMaster: ProjectSiteMaster[] = [
  { site_id: 'PRJ-2024-001', site_name: 'Commercial Complex - Tower A', status: 'ACTIVE' },
  { site_id: 'PRJ-2024-002', site_name: 'Commercial Complex - Tower B', status: 'ACTIVE' },
  { site_id: 'PRJ-2024-003', site_name: 'Residential Plaza - Block A', status: 'ACTIVE' },
  { site_id: 'PRJ-2024-004', site_name: 'Hospital Extension Wing', status: 'ACTIVE' },
  { site_id: 'PRJ-2024-005', site_name: 'Metro Line Station C', status: 'ACTIVE' },
  { site_id: 'PRJ-2024-006', site_name: 'Jumeirah Luxury AMC Site', status: 'COMPLETED' }
];

export const mockUsersMaster: UserMaster[] = [
  { id: 'user1', name: 'Ahmed Al-Mansouri', role: 'ADMIN', email: 'ahmed.m@mepify.com', designation: 'Project Director' },
  { id: 'user2', name: 'John Smith', role: 'CONTRACTOR_REP', email: 'john.s@mepify.com', designation: 'QA Manager' },
  { id: 'user3', name: 'Sarah Johnson', role: 'CLIENT_REP', email: 'sarah.j@dubaiproperties.ae', designation: 'Client Representative' },
  { id: 'user4', name: 'Michael Chen', role: 'OEM_REP', email: 'michael.c@mepify.com', designation: 'Contract Director' },
  { id: 'user5', name: 'David Miller', role: 'CONTRACTOR_REP', email: 'david.m@mepify.com', designation: 'Project Manager' },
  { id: 'user6', name: 'Emma Wilson', role: 'ADMIN', email: 'emma.w@mepify.com', designation: 'Contract Admin' },
  { id: 'user7', name: 'Hassan Abdullah', role: 'CLIENT_REP', email: 'hassan.a@emaar.ae', designation: 'Director of Facilities' },
  { id: 'usr-sys-admin', name: 'System Admin', role: 'ADMIN', email: 'admin@mepify.com', designation: 'Platform Administrator' },
  { id: 'usr-emma-wilson', name: 'Emma Wilson', role: 'ADMIN', email: 'emma.w@mepify.com', designation: 'Contract Admin' },
  { id: 'usr-ahmed-al-mansouri', name: 'Ahmed Al-Mansouri', role: 'ADMIN', email: 'ahmed.m@mepify.com', designation: 'Project Director' },
  { id: 'usr-john-smith', name: 'John Smith', role: 'CONTRACTOR_REP', email: 'john.s@mepify.com', designation: 'QA Manager' },
  { id: 'usr-hassan-abdullah', name: 'Hassan Abdullah', role: 'CLIENT_REP', email: 'hassan.a@emaar.ae', designation: 'Director of Facilities' },
  { id: 'usr-jitanshu-shaw', name: 'Jitanshu Shaw', role: 'ADMIN', email: 'jitanshu.shaw@fitnearn.com', designation: 'Tenant Owner' }
];

export function resolveActorInfo(
  actorRaw?: string | null,
  idRaw?: string | null
): { name: string; id: string | null; designation?: string } {
  const actor = (actorRaw || '').trim();
  const rawId = (idRaw || '').trim();

  // If both empty
  if (!actor && !rawId) {
    return { name: 'System User', id: null };
  }

  // 1. Try finding in mockUsersMaster by exact ID or case-insensitive ID
  const searchId = rawId || actor;
  const userById = mockUsersMaster.find(u => u.id.toLowerCase() === searchId.toLowerCase());
  if (userById) {
    return {
      name: userById.name,
      id: userById.id,
      designation: userById.designation
    };
  }

  // 2. Try finding in mockUsersMaster by Name
  const searchName = actor || rawId;
  const userByName = mockUsersMaster.find(
    u => u.name.toLowerCase() === searchName.toLowerCase() ||
         searchName.toLowerCase().startsWith(u.name.toLowerCase()) ||
         u.name.toLowerCase().startsWith(searchName.toLowerCase())
  );
  if (userByName) {
    return {
      name: userByName.name,
      id: userByName.id,
      designation: userByName.designation
    };
  }

  // 3. Special handling for "Jitanshu" -> "Jitanshu Shaw" & "usr-jitanshu-shaw"
  if (actor.toLowerCase().includes('jitanshu') || rawId.toLowerCase().includes('jitanshu')) {
    return {
      name: 'Jitanshu Shaw',
      id: (rawId && rawId.toLowerCase() !== 'jitanshu') ? rawId : 'usr-jitanshu-shaw',
      designation: 'Tenant Owner'
    };
  }

  // 4. Identify if value is an internal ID vs a human name
  const isActorAnId = actor.startsWith('usr-') || actor.startsWith('user') || /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(actor);
  const isRawIdAnId = rawId.startsWith('usr-') || rawId.startsWith('user') || /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(rawId);

  if (isActorAnId) {
    return {
      name: (rawId && !isRawIdAnId) ? rawId : actor,
      id: actor,
      designation: undefined
    };
  }

  // actor is a human name (e.g. "Emma Wilson", "Alex")
  // Only provide ID if rawId is a genuinely distinct identifier
  const validId = (isRawIdAnId || (rawId && rawId !== actor)) ? rawId : null;
  return {
    name: actor,
    id: validId,
    designation: undefined
  };
}

// Core Contracts Table
export const mockContracts: DbContract[] = [
  {
    id: '1',
    contract_number: 'MC-2024-88912',
    name: 'Commercial HVAC Installation & Testing',
    client_name: 'Dubai Properties Group',
    client_primary_contact_name: 'Sarah Johnson',
    client_primary_contact_phone: '+971 50 123 4567',
    client_primary_contact_email: 'sarah.j@dubaiproperties.ae',
    contract_type: 'EPC',
    start_date: '2024-01-15',
    planned_end_date: '2027-06-30',
    actual_end_date: null,
    status: 'ACTIVE',
    last_modified: '2024-12-18',
    contract_value: 2500000,
    contract_value_currency: 'USD',
    retention_percentage: 10,
    performance_guarantee: 5,
    country: 'UAE',
    state: 'Dubai',
    city: 'Dubai Marina',
    pincode: '00000',
    address: 'Tower B, Marina Gate, Dubai Marina',
    organization_id: 'org-mepify-001',
    organization_name: 'MEPify Global Solutions',
    contract_admin_user_id: 'user6',
    is_deleted: false,
    deleted_at: null,
    deleted_by: null,
    completed_at: null,
    completed_by: null,
    archived_at: null,
    archived_by: null,
    archive_reason: null
  },
  {
    id: '2',
    contract_number: 'MC-2024-51203',
    name: 'Skyline Heights Electrical Operations',
    client_name: 'Lodha Group',
    client_primary_contact_name: 'Amit Patel',
    client_primary_contact_phone: '+91 22 9876 5432',
    client_primary_contact_email: 'amit.p@lodhagroup.com',
    contract_type: 'O&M',
    start_date: '2024-03-01',
    planned_end_date: '2027-12-31',
    actual_end_date: null,
    status: 'ACTIVE',
    last_modified: '2024-12-19',
    contract_value: 65000000,
    contract_value_currency: 'INR',
    retention_percentage: 8,
    performance_guarantee: 5,
    country: 'India',
    state: 'Maharashtra',
    city: 'Mumbai',
    pincode: '400011',
    address: 'Lodha Park, Senapati Bapat Marg, Lower Parel',
    organization_id: 'org-mepify-001',
    organization_name: 'MEPify Global Solutions',
    contract_admin_user_id: 'user6',
    is_deleted: false,
    deleted_at: null,
    deleted_by: null,
    completed_at: null,
    completed_by: null,
    archived_at: null,
    archived_by: null,
    archive_reason: null
  },
  {
    id: '3',
    contract_number: 'MC-2024-33102',
    name: 'Marina Heights MEP Support Contract',
    client_name: 'Emaar Properties',
    client_primary_contact_name: 'Hassan Abdullah',
    client_primary_contact_phone: '+971 50 998 8776',
    client_primary_contact_email: 'hassan.a@emaar.ae',
    contract_type: 'SLA',
    start_date: '2024-04-10',
    planned_end_date: '2027-08-15',
    actual_end_date: null,
    status: 'ACTIVE',
    last_modified: '2024-12-10',
    contract_value: 1850000,
    contract_value_currency: 'USD',
    retention_percentage: 10,
    performance_guarantee: 5,
    country: 'UAE',
    state: 'Dubai',
    city: 'Downtown Dubai',
    pincode: '00000',
    address: 'Emaar Square, Building 3',
    organization_id: 'org-mepify-001',
    organization_name: 'MEPify Global Solutions',
    contract_admin_user_id: 'user6',
    is_deleted: false,
    deleted_at: null,
    deleted_by: null,
    completed_at: null,
    completed_by: null,
    archived_at: null,
    archived_by: null,
    archive_reason: null
  },
  {
    id: '4',
    contract_number: 'MC-2024-91283',
    name: 'Tech Park Fire Safety Compliance',
    client_name: 'Tata Consultancy Services',
    client_primary_contact_name: 'Priya Sharma',
    client_primary_contact_phone: '+91 80 1234 5678',
    client_primary_contact_email: 'priya.sharma@tcs.com',
    contract_type: 'EPC',
    start_date: '2024-08-01',
    planned_end_date: '2024-12-20',
    actual_end_date: null,
    status: 'ACTIVE',
    last_modified: '2024-12-17',
    contract_value: 85000000,
    contract_value_currency: 'INR',
    retention_percentage: 10,
    performance_guarantee: 5,
    country: 'India',
    state: 'Karnataka',
    city: 'Bangalore',
    pincode: '560001',
    address: 'ITPL Main Road, Whitefield',
    organization_id: 'org-mepify-001',
    organization_name: 'MEPify Global Solutions',
    contract_admin_user_id: 'user6',
    is_deleted: false,
    deleted_at: null,
    deleted_by: null,
    completed_at: null,
    completed_by: null,
    archived_at: null,
    archived_by: null,
    archive_reason: null
  },
  {
    id: '5',
    contract_number: 'MC-2025-44123',
    name: 'Jumeirah Residential MEP Sign-off',
    client_name: 'Nakheel Properties',
    client_primary_contact_name: 'Fatima Al-Hashemi',
    client_primary_contact_phone: '+971 4 390 3333',
    client_primary_contact_email: 'fatima.h@nakheel.ae',
    contract_type: 'TURNKEY',
    start_date: '2025-01-01',
    planned_end_date: '2025-10-30',
    actual_end_date: '2025-10-30',
    status: 'COMPLETED',
    last_modified: '2025-10-30',
    contract_value: 9800000,
    contract_value_currency: 'USD',
    retention_percentage: 5,
    performance_guarantee: 5,
    country: 'UAE',
    state: 'Dubai',
    city: 'Jumeirah Islands',
    pincode: '00000',
    address: 'Clubhouse, Jumeirah Islands',
    organization_id: 'org-mepify-001',
    organization_name: 'MEPify Global Solutions',
    contract_admin_user_id: 'user15',
    is_deleted: false,
    deleted_at: null,
    deleted_by: null,
    completed_at: '2025-10-30',
    completed_by: 'usr-sys-admin',
    archived_at: null,
    archived_by: null,
    archive_reason: null
  },
  {
    id: '6',
    contract_number: 'MC-2022-11092',
    name: 'Old Downtown Commercial Hub SLA',
    client_name: 'Meraas Holding',
    client_primary_contact_name: 'Maria Garcia',
    client_primary_contact_phone: '+971 50 111 2222',
    client_primary_contact_email: 'maria.g@meraas.ae',
    contract_type: 'SLA',
    start_date: '2022-04-10',
    planned_end_date: '2023-10-30',
    actual_end_date: '2023-10-30',
    status: 'ARCHIVED',
    last_modified: '2023-10-30',
    contract_value: 3500000,
    contract_value_currency: 'USD',
    retention_percentage: 10,
    performance_guarantee: 5,
    country: 'UAE',
    state: 'Dubai',
    city: 'City Walk',
    pincode: '00000',
    address: 'City Walk, Al Wasl',
    organization_id: 'org-mepify-001',
    organization_name: 'MEPify Global Solutions',
    contract_admin_user_id: 'user6',
    is_deleted: false,
    deleted_at: null,
    deleted_by: null,
    completed_at: '2023-10-30',
    completed_by: 'usr-sys-admin',
    archived_at: '2023-10-30',
    archived_by: 'usr-sys-admin',
    archive_reason: 'Completed schedule closure'
  }
];

// Relation Bridge Lists
export const mockContractScopeSystems: DbContractScopeSystem[] = [
  { contract_id: '1', system: 'HVAC' },
  { contract_id: '1', system: 'ELECTRICAL' },
  { contract_id: '2', system: 'ELECTRICAL' },
  { contract_id: '3', system: 'HVAC' },
  { contract_id: '3', system: 'PLUMBING' },
  { contract_id: '4', system: 'FIRE_SAFETY' },
  { contract_id: '5', system: 'HVAC' },
  { contract_id: '5', system: 'PLUMBING' },
  { contract_id: '5', system: 'FIRE_SAFETY' },
  { contract_id: '6', system: 'HVAC' },
  { contract_id: '6', system: 'ELECTRICAL' },
  { contract_id: '6', system: 'BMS' }
];

export const mockContractTeamAssignments: DbContractTeamAssignment[] = [
  { id: 'asg-2', contract_id: '1', user_id: 'user1', role: 'PROJECT_DIRECTOR', assigned_at: '2024-01-15' },
  { id: 'asg-3', contract_id: '1', user_id: 'user2', role: 'QA_MANAGER', assigned_at: '2024-01-15' },
  { id: 'asg-7', contract_id: '2', user_id: 'user1', role: 'PROJECT_DIRECTOR', assigned_at: '2024-03-01' },
  { id: 'asg-8', contract_id: '2', user_id: 'user2', role: 'QA_MANAGER', assigned_at: '2024-03-01' },
  { id: 'asg-11', contract_id: '3', user_id: 'user1', role: 'PROJECT_DIRECTOR', assigned_at: '2024-04-10' },
  { id: 'asg-12', contract_id: '3', user_id: 'user2', role: 'QA_MANAGER', assigned_at: '2024-04-10' },
  { id: 'asg-15', contract_id: '4', user_id: 'user1', role: 'PROJECT_DIRECTOR', assigned_at: '2024-08-01' },
  { id: 'asg-16', contract_id: '4', user_id: 'user2', role: 'QA_MANAGER', assigned_at: '2024-08-01' }
];

export const mockContractClientRepresentatives: DbContractClientRepresentative[] = [
  { id: 'asg-4', contract_id: '1', user_id: 'user3', assigned_at: '2024-01-20' },
  { id: 'asg-9', contract_id: '2', user_id: 'user3', assigned_at: '2024-03-01' },
  { id: 'asg-13', contract_id: '3', user_id: 'user3', assigned_at: '2024-04-10' },
  { id: 'asg-17', contract_id: '4', user_id: 'user3', assigned_at: '2024-08-01' }
];

export const mockContractProjectSites: DbContractProjectSite[] = [
  { contract_id: '1', site_id: 'PRJ-2024-001', assigned_at: '2024-01-15', assigned_by: 'usr-sys-admin' },
  { contract_id: '1', site_id: 'PRJ-2024-002', assigned_at: '2024-01-15', assigned_by: 'usr-sys-admin' },
  { contract_id: '2', site_id: 'PRJ-2024-004', assigned_at: '2024-03-01', assigned_by: 'usr-sys-admin' },
  { contract_id: '5', site_id: 'PRJ-2024-006', assigned_at: '2025-01-01', assigned_by: 'usr-sys-admin' }
];

export const mockContractAdminHistory: DbContractAdminHistory[] = [
  {
    id: 'h-1',
    contract_id: '1',
    user_id: 'user6',
    full_name_snapshot: 'Emma Wilson',
    designation_snapshot: 'Contract Admin',
    email_snapshot: 'emma.w@mepify.com',
    assigned_at: '2024-01-15',
    assigned_by: 'usr-sys-admin',
    transfer_reason: 'Initial assignment',
    revoked_at: null
  },
  {
    id: 'h-2',
    contract_id: '2',
    user_id: 'user6',
    full_name_snapshot: 'Emma Wilson',
    designation_snapshot: 'Contract Admin',
    email_snapshot: 'emma.w@mepify.com',
    assigned_at: '2024-03-01',
    assigned_by: 'usr-sys-admin',
    transfer_reason: 'Initial assignment',
    revoked_at: null
  },
  {
    id: 'h-3',
    contract_id: '3',
    user_id: 'user6',
    full_name_snapshot: 'Emma Wilson',
    designation_snapshot: 'Contract Admin',
    email_snapshot: 'emma.w@mepify.com',
    assigned_at: '2024-04-10',
    assigned_by: 'usr-sys-admin',
    transfer_reason: 'Initial assignment',
    revoked_at: null
  },
  {
    id: 'h-4',
    contract_id: '4',
    user_id: 'user6',
    full_name_snapshot: 'Emma Wilson',
    designation_snapshot: 'Contract Admin',
    email_snapshot: 'emma.w@mepify.com',
    assigned_at: '2024-08-01',
    assigned_by: 'usr-sys-admin',
    transfer_reason: 'Initial assignment',
    revoked_at: null
  },
  {
    id: 'h-5',
    contract_id: '5',
    user_id: 'user15',
    full_name_snapshot: 'Emma Wilson',
    designation_snapshot: 'Contract Admin',
    email_snapshot: 'emma.w@mepify.com',
    assigned_at: '2025-01-01',
    assigned_by: 'usr-sys-admin',
    transfer_reason: 'Initial assignment',
    revoked_at: null
  },
  {
    id: 'h-6',
    contract_id: '6',
    user_id: 'user6',
    full_name_snapshot: 'Emma Wilson',
    designation_snapshot: 'Contract Admin',
    email_snapshot: 'emma.w@mepify.com',
    assigned_at: '2022-04-10',
    assigned_by: 'usr-sys-admin',
    transfer_reason: 'Initial assignment',
    revoked_at: null
  }
];

export const mockContractRevisions: DbContractRevision[] = [
  { id: 'rev-1', contract_id: '1', revision_number: 1, changed_at: '2024-01-15 10:00 AM', changed_by: 'usr-sys-admin', change_severity: 'MINOR' },
  { id: 'rev-2', contract_id: '1', revision_number: 2, changed_at: '2024-06-20 02:30 PM', changed_by: 'usr-ahmed-al-mansouri', change_severity: 'MAJOR' },
  { id: 'rev-3', contract_id: '1', revision_number: 3, changed_at: '2024-12-18 11:00 AM', changed_by: 'usr-emma-wilson', change_severity: 'MAJOR' },
  { id: 'rev-4', contract_id: '3', revision_number: 1, changed_at: '2024-01-15 09:00 AM', changed_by: 'usr-sys-admin', change_severity: 'MINOR' },
  { id: 'rev-5', contract_id: '3', revision_number: 2, changed_at: '2024-11-05 11:15 AM', changed_by: 'usr-john-smith', change_severity: 'MAJOR' },
  { id: 'rev-6', contract_id: '6', revision_number: 1, changed_at: '2022-03-01 09:00 AM', changed_by: 'usr-sys-admin', change_severity: 'MINOR' },
  { id: 'rev-7', contract_id: '6', revision_number: 2, changed_at: '2022-09-15 04:00 PM', changed_by: 'usr-emma-wilson', change_severity: 'MAJOR' },
  { id: 'rev-8', contract_id: '6', revision_number: 3, changed_at: '2023-04-10 10:45 AM', changed_by: 'usr-hassan-abdullah', change_severity: 'MAJOR' }
];

export const mockContractRevisionChanges: DbContractRevisionChange[] = [
  { revision_id: 'rev-1', field: 'name', old_value: '', new_value: 'Commercial HVAC Installation & Testing' },
  { revision_id: 'rev-2', field: 'planned_end_date', old_value: '2027-06-30', new_value: '2027-08-15' },
  { revision_id: 'rev-3', field: 'planned_end_date', old_value: '2027-08-15', new_value: '2027-12-31' },
  { revision_id: 'rev-5', field: 'planned_end_date', old_value: '2027-08-15', new_value: '2027-11-05' },
  { revision_id: 'rev-7', field: 'planned_end_date', old_value: '2023-04-10', new_value: '2022-09-15' },
  { revision_id: 'rev-8', field: 'planned_end_date', old_value: '2022-09-15', new_value: '2023-10-30' }
];

export const mockContractRelationAudit: DbContractRelationAudit[] = [];

export const mockContractMetrics: DbContractMetrics[] = [
  {
    contract_id: '1',
    approved_submissions: 38,
    total_submissions: 52,
    completed_sites: 0,
    total_sites: 2,
    valid_equipment: 14,
    total_equipment: 20,
    pending_signatures_total: 3,
    pending_signatures_overdue: 2
  },
  {
    contract_id: '2',
    approved_submissions: 9,
    total_submissions: 20,
    completed_sites: 0,
    total_sites: 1,
    valid_equipment: 0,
    total_equipment: 0,
    pending_signatures_total: 0,
    pending_signatures_overdue: 0
  },
  {
    contract_id: '3',
    approved_submissions: 5,
    total_submissions: 20,
    completed_sites: 0,
    total_sites: 0,
    valid_equipment: 3,
    total_equipment: 8,
    pending_signatures_total: 5,
    pending_signatures_overdue: 1
  },
  {
    contract_id: '4',
    approved_submissions: 45,
    total_submissions: 50,
    completed_sites: 0,
    total_sites: 0,
    valid_equipment: 18,
    total_equipment: 20,
    pending_signatures_total: 1,
    pending_signatures_overdue: 0
  },
  {
    contract_id: '5',
    approved_submissions: 30,
    total_submissions: 30,
    completed_sites: 1,
    total_sites: 1,
    valid_equipment: 5,
    total_equipment: 5,
    pending_signatures_total: 0,
    pending_signatures_overdue: 0
  },
  {
    contract_id: '6',
    approved_submissions: 30,
    total_submissions: 30,
    completed_sites: 0,
    total_sites: 0,
    valid_equipment: 0,
    total_equipment: 0,
    pending_signatures_total: 0,
    pending_signatures_overdue: 0
  }
];

// Helper to simulate lookup
export const getContractById = (id: string) => {
  return mockContracts.find(c => c.id === id);
};
